import { useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import {
  IconHamburgerMenuDefault,
  Modal,
  ModalClose,
  ModalContent,
} from '@mc/design-system'
import { useFlw1235EnterpriseNavCtaOverride } from 'experiments/flw-1235_v2_enterprise_nav_cta'
import {
  CertificatesPromoBadge,
  CertsPromoCard,
  useCoreNavPromoSignalExp,
} from 'experiments/mccerts-2851_v1_core_nav_promo_signal'
import {
  LatestCertificateInfo,
  type CertificateInfo,
} from 'pages/certificates/common/misc'
import { useCertificateProgramCatalog } from 'pages/certificates/hooks'
import { TrackClick } from 'pages/certificates/tracking/certificatesTracking'
import { useIsAllAccessEnabled } from '../../../../../experiments/prcng-897_v1_standalone-bundle'
import { useIsNewForBusinessPageEnabled } from '../../hooks'
import { getProducts, productTypeTrackingMap } from '../../mobileMenuProducts'
import { AccordionMenu } from '../AccordionMenu'
import { CertificateCallout } from './CertificateCallout'
import { CertificatesSection } from './CertificatesSection'
import classes from './CertificatesMobileMenu.module.scss'

interface CertificatesMobileMenuProps {
  certificates?: CertificateInfo[]
  latestCertificates?: LatestCertificateInfo[]
  currentCertificateTitle?: string
  currentCertificateImg?: { url?: string; altText?: string }
  enrollDate?: string | null
  newForBusinessPageEnabled?: boolean
  signupEnabled?: boolean
}

export const CertificatesMobileMenu = ({
  currentCertificateTitle,
  currentCertificateImg,
  signupEnabled = false,
}: CertificatesMobileMenuProps) => {
  const { t } = useTranslation('@mc/certificates')
  const [showCertificatesSection, setShowCertificatesSection] = useState(false)
  const { pathname, query } = useRouter()
  const newForBusinessPageEnabled = useIsNewForBusinessPageEnabled()
  const { isEnabled: isAllAccessEnabled } = useIsAllAccessEnabled()
  const { tiles, isError, isLoading } = useCertificateProgramCatalog('nav')

  const [show, setShow] = useState(false)

  // mccerts-2851_v1_core_nav_promo_signal
  const promoSignalNavExp = useCoreNavPromoSignalExp(show)
  const enterpriseCtaCopy = useFlw1235EnterpriseNavCtaOverride()

  const onShowCertificatesSection = () => setShowCertificatesSection(true)
  const onHideCertificatesSection = () => setShowCertificatesSection(false)
  const onClose = () => {
    setShowCertificatesSection(false)
    setShow(false)
  }

  const products = getProducts({
    pathname,
    query: query as Record<string, string>,
    newForBusinessPageEnabled,
    onShowCertificatesSection,
    onCallLinksEnabled: true,
    allAccessEnabled: isAllAccessEnabled,
    enterpriseCtaCopy,
    // mccerts-2851_v1_core_nav_promo_signal
    certificatesBadge: promoSignalNavExp.enabled ? (
      <CertificatesPromoBadge text={promoSignalNavExp.menu.badgeText} />
    ) : undefined,
    certificatesPromoCard: promoSignalNavExp.enabled ? (
      <CertsPromoCard
        title={promoSignalNavExp.menu.title}
        subtitle={promoSignalNavExp.menu.subtitle}
        product={productTypeTrackingMap.certificates}
      />
    ) : undefined,
    Callout: currentCertificateTitle && (
      <CertificateCallout
        currentCertificateTitle={currentCertificateTitle}
        currentCertificateImg={currentCertificateImg}
        signupEnabled={signupEnabled}
      />
    ),
    productType: 'certificates',
  })

  return (
    <div className='d-lg-none mc-theme-dark'>
      <TrackClick event='certificatesHamburgerMenuClicked'>
        <button
          className='mc-theme-light'
          onClick={() => setShow(true)}
          aria-label={t('certificatesMenuMobile.aria.openNavigationMenu')}
        >
          <IconHamburgerMenuDefault size='sm' />
        </button>
      </TrackClick>
      <Modal
        appendToBody
        show={show}
        onClose={() => setShow(false)}
        className='mc-theme-dark mc-bg-container-tint-dark'
      >
        <ModalContent className={classes.absolute}>
          <div
            className='d-flex flex-row-reverse'
            style={{
              borderBottom: '1px solid var(--mc-theme-dropdown-border)',
            }}
          >
            <ModalClose className='position-relative' />
          </div>
          {showCertificatesSection ? (
            <CertificatesSection
              tiles={tiles}
              isError={isError}
              isLoading={isLoading}
              onClose={onClose}
              onHideCertificatesSection={onHideCertificatesSection}
            />
          ) : (
            <>
              <AccordionMenu
                products={products}
                defaultExpandedProduct='certificates'
                productSectionClassName='mc-bg-container-tint-medium'
              />
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  )
}
